import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import Header from '@/components/shared/Header';
import ExploreOurStoriesGrid from '@/components/ExploreOurStoriesGrid';
import Footer from '@/components/Footer';

import Button, { RoundButton } from '@/components/shared/Button';
import Typography from '../components/shared/Typography';
import { setCurrentStepPah } from '@/store/actions/currentStepPah';

import testimonialCarouselData, {
  TestimonialData
} from '@/components/TestimonialCarousel/data';

import TestimonialCarousel from '@/components/TestimonialCarousel';
import CreateAvatar from '@/components/CreateAvatar';
import PrintAtHome from '@/components/PrintAtHome';
import {
  fetchProductList,
  initializeCart,
  loadPreviewRelatedContent,
  populateCharacterConfigured,
  populateCurrentStep,
  populateCurrentStepPah,
  populatePrintAtHome
} from '@/store/initializeState';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';

const Shop: React.FC = () => {
  const dispatch = useDispatch();

  const { productList, configSetupBuildKey, productCart, characterConfigured } =
    useSelector((state: RootStateOrAny) => state);

  const [showPahModal, setShowPahModal] = useState<boolean>(false);

  const [showCharacterBuilderModal, setShowCharacterBuilderModal] =
    useState<boolean>(false);

  const [redirect, setRedirect] = useState<string>('');

  useEffect(() => {
    loadPreviewRelatedContent(configSetupBuildKey, dispatch);
    fetchProductList(productList, dispatch);
    initializeCart(productCart, dispatch);
    populateCharacterConfigured(dispatch);
    populateCurrentStep(dispatch);
    populateCurrentStepPah(dispatch);
    populatePrintAtHome(dispatch);
  }, []);

  return (
    <div>
      <Head>
        <title>Keepsake Tales | Shop</title>
        <meta
          name="description"
          content="At Keepsake Tales, we exist to help every child understand that they are one of a kind. We make each child the main character of their own story so that every child will know how much they matter."
        />
        <meta
          property="og:title"
          content="Your Child is a CHAMPION - Show Them | Keepsake Tales | Shop"
        />
        <meta
          property="og:description"
          content="At Keepsake Tales, we exist to help every child understand that they are one of a kind. We make each child the main character of their own story so that every child will know how much they matter."
        />
        <meta property="og:type" content="books.book" />
        <meta property="og:url" content="https://www.keepsaketales.com/shop" />
        <meta
          property="og:image"
          content="https://www.keepsaketales.com/keepsake-tales.png"
        />
        <meta
          property="og:image:secure_url"
          content="https://www.keepsaketales.com/keepsake-tales.png"
        />
        <link rel="icon" href="/favicon.ico" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        ></meta>
      </Head>
      <Header
        currentPage="shop"
        setShowCharacterBuilderModal={setShowCharacterBuilderModal}
        setRedirect={setRedirect}
      />
      <main>
        <ExploreOurStoriesGrid
          setShowCharacterBuilderModal={setShowCharacterBuilderModal}
          setRedirect={setRedirect}
        />
        {/* flex - lineHeigth: 95, */}
        <div
          className="h-200 px-1 py-8 text-center lg:flex"
          style={{
            background: '#6BBCFF'
          }}
        >
          <Typography
            className="m-auto mb-8 lg:m-auto"
            type="b1"
            style={{ color: '#fff' }}
          >
            Choose a print at home design and download a nice card with your
            character!
          </Typography>
          <Button
            className="block m-auto w-62 h-14 cursor-pointer"
            color="white"
            textColor="primary"
            onClick={() => {
              if (characterConfigured) {
                setCurrentStepPah(1);
                setShowPahModal(true);
              } else {
                setRedirect('pahModal');
                setShowCharacterBuilderModal(true);
              }
              // TODO: trackPrintAtHome
            }}
            aria-label="FREE POSTERS"
          >
            <div className="flex justify-evenly items-center">
              <span
                className="font-extrabold text-14 ml-2 font-sans"
                style={{ fontFamily: 'Nunito', fontWeight: 800 }}
              >
                FREE POSTERS
              </span>
              <RoundButton
                aria-label="previous"
                style={{
                  height: 36,
                  width: 36,
                  marginLeft: 12
                }}
              >
                {/* <Icon icon="printer" color="white" /> */}
                {/* TODO: Fix path code hack on contants.tsx */}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.2871 6.13118H15.4092V4.38656L11.0345 0H5.89453C5.54904 0.000373165 5.21782 0.136691 4.97352 0.379053C4.72922 0.621415 4.59181 0.950016 4.59143 1.29277V6.13118H2.71352C2.19812 6.13174 1.70398 6.3351 1.33953 6.69666C0.975082 7.05822 0.770095 7.54844 0.769531 8.05976V14.179C0.770095 14.6903 0.975082 15.1805 1.33953 15.5421C1.70398 15.9036 2.19812 16.107 2.71352 16.1076H3.77362L3.2592 18.1003C3.19699 18.3362 3.18955 18.5831 3.23742 18.8223C3.2853 19.0614 3.38725 19.2867 3.5356 19.4812C3.65666 19.6408 3.81314 19.7706 3.99291 19.8606C4.17268 19.9505 4.37091 19.9983 4.57225 20H15.4283C15.6297 19.9983 15.8279 19.9505 16.0077 19.8606C16.1875 19.7706 16.3439 19.6408 16.465 19.4812C16.6133 19.2866 16.7151 19.0611 16.7629 18.8218C16.8106 18.5825 16.803 18.3356 16.7407 18.0996L16.227 16.1076H17.2871C17.8025 16.107 18.2966 15.9036 18.6611 15.5421C19.0255 15.1805 19.2305 14.6903 19.2311 14.179V8.05976C19.2305 7.54844 19.0255 7.05822 18.6611 6.69666C18.2966 6.3351 17.8025 6.13174 17.2871 6.13118ZM11.4487 2.41919L12.9543 3.92979H11.8175C11.7205 3.9296 11.6276 3.89127 11.5592 3.82321C11.4907 3.75515 11.4523 3.66292 11.4523 3.56676L11.4487 2.41919ZM6.01248 1.40979H10.0276V3.56676C10.028 4.0367 10.2163 4.4873 10.5512 4.81967C10.8861 5.15204 11.3402 5.33902 11.8139 5.33958H13.9881V6.13118H6.01248V1.40979ZM15.3573 18.5902H4.6433C4.62574 18.5452 4.62325 18.4958 4.6362 18.4492L5.99258 13.1921H14.008L15.3644 18.4492C15.377 18.4958 15.3745 18.5451 15.3573 18.5902ZM17.81 14.179C17.8098 14.3165 17.7547 14.4484 17.6566 14.5456C17.5586 14.6429 17.4257 14.6976 17.2871 14.6978H15.866L15.4774 13.1921C15.6658 13.1921 15.8465 13.1178 15.9798 12.9856C16.113 12.8535 16.1879 12.6742 16.1879 12.4872C16.1879 12.3003 16.113 12.121 15.9798 11.9888C15.8465 11.8566 15.6658 11.7823 15.4774 11.7823H4.52394C4.33549 11.7823 4.15477 11.8566 4.02152 11.9888C3.88827 12.121 3.81341 12.3003 3.81341 12.4872C3.81341 12.6742 3.88827 12.8535 4.02152 12.9856C4.15477 13.1178 4.33549 13.1921 4.52394 13.1921L4.13598 14.6978H2.71494C2.5763 14.6976 2.4434 14.6429 2.34537 14.5456C2.24734 14.4484 2.19218 14.3165 2.19199 14.179V8.05976C2.19218 7.92222 2.24734 7.79037 2.34537 7.69312C2.4434 7.59586 2.5763 7.54116 2.71494 7.54097H17.2885C17.4271 7.54116 17.56 7.59586 17.6581 7.69312C17.7561 7.79037 17.8113 7.92222 17.8114 8.05976L17.81 14.179Z"
                    fill="white"
                  />
                </svg>
              </RoundButton>
            </div>
          </Button>
        </div>
        <div className="flex box-border">
          <TestimonialCarousel
            className="bg-lightBlue"
            list={testimonialCarouselData as TestimonialData[]}
          />
        </div>
        <Footer />
        <CreateAvatar
          showCharacterBuilderModal={showCharacterBuilderModal}
          setShowCharacterBuilderModal={setShowCharacterBuilderModal}
          setShowPahModal={setShowPahModal}
          redirect={redirect}
        />
        <PrintAtHome
          showPahModal={showPahModal}
          setShowPahModal={setShowPahModal}
        />
      </main>
    </div>
  );
};

export default Shop;
